import { chain, findKey, map, transform } from 'lodash'
import { siteSettingsPolicy } from '@/policies';
import { siteSettingsService } from '@/services';

(function (angular) {
    'use strict';

    angular.module('app')
        .controller('adminPromotionsController', adminPromotionsController);

    adminPromotionsController.$inject = [
        '$scope',
        'Promotions',
        'PromotionsApi',
        'SettingsApi',
        'alerts',
        'thisSiteSettings',
    ];

    function adminPromotionsController(
        $scope,
        Promotions,
        PromotionsApi,
        SettingsApi,
        alerts,
        thisSiteSettings,
    ) {
        const vm = this;

        // properties
        vm.policyChecks = {
            canManageSettings: siteSettingsPolicy.canManage(),
        };
        vm.isLoading = false;
        vm.promotions = {};
        vm.filters = {
            page_size: 15,
            page: 1,
            sort: 'created_at',
            status: '',
            title: '',
            with: ['user'],
        };
        vm.settings = {
            tiers: {},
            default: null,
        };
        vm.charge = {
            enabled: false,
            online: false,
        };
        vm.chargeOptions = [
            {label: 'Yes', value: true},
            {label: 'No', value: false},
        ];
        vm.statusOptions = [
            {label: 'All', value: ''},
            {label: 'Pending', value: 'pending'},
            {label: 'Approved', value: 'approved'},
            {label: 'Rejected', value: 'rejected'}
        ];
        vm.editedPendingPromotions = []; // holds the ID's of pending promotions, in case they're approved, so we can add a style
        vm.currencySymbol = thisSiteSettings.currency_symbol;
        
        // methods
        vm.getPromotions = getPromotions;
        vm.createPromotion = createPromotion;
        vm.editPromotion = editPromotion;

        vm.getPromotionSettings = getPromotionSettings;
        vm.setPromotionSettings = setPromotionSettings;

        /**
         * Initialises the controller.
         *
         * @returns {Promise<void>}
         */
        async function bootstrap(): Promise<void> {
            getPromotions();
            await getPromotionSettings();
        }

        /**
         * Retrieves settings from the API. Shame we can't specify an array of keys.
         *
         * @return {Promise<void>}
         */
        async function getPromotionSettings(): Promise<void> {
            await siteSettingsService.load();
            const {
                promotion_charge_enabled,
                promotion_charge_accept_online_payments,
                promotion_tiers,
            } = siteSettingsService.all();

            vm.charge.enabled = promotion_charge_enabled;
            vm.charge.online = promotion_charge_accept_online_payments;
            vm.settings.default = findKey(promotion_tiers, { 'default': true });
            vm.settings.tiers = transformTiers(promotion_tiers);

            $scope.$digest();
        }

        /**
         * Transforms tiers into an array this controller can use.
         *
         * @param tiers
         */
        function transformTiers(tiers): any[] {
            return chain(tiers)
                .map((tier, key) => ({ key, ...tier }))
                .orderBy('level', 'desc')
                .value();
        }

        /**
         * Sets settings to the API.
         * @return {void}
         */
        function setPromotionSettings() {
            const promotion_tiers = chain(vm.settings.tiers)
                .reduce((tiers, { key, enabled, label, price }) => {
                    return {
                        ...tiers,
                        [key]: { 
                            default: vm.settings.default === key,
                            enabled,
                            label,
                            price,
                        },
                    };
                }, {})
                .value();

            SettingsApi.save({
                settings: {
                    promotion_charge_enabled: vm.charge.enabled,
                    promotion_charge_accept_online_payments: vm.charge.online,
                    promotion_tiers,
                },
            }, function (response) {
                if (response && response.data && response.data.promotion_charge_enabled) {
                    vm.charge.enabled = response.data.promotion_charge_enabled;
                    vm.charge.online = response.data.promotion_charge_accept_online_payments;
                    vm.settings.tiers = transformTiers(response.data.promotion_tiers);
                }

                alerts.emit({
                    theme: 'success',
                    title: 'Settings updated.'
                });
            });
        }

        /**
         * Retrieves promotions from the API
         * @return {void}
         */
        function getPromotions() {
            vm.isLoading = true;

            PromotionsApi.get(vm.filters, function handleGetPromotionsResponse(response) {
                vm.promotions = response;
                vm.isLoading = false;
            }, function handleGetPromotionsError() {
                vm.isLoading = false;
            });
        }

        /**
         * Opens create promotion modal, then refreshes the table
         * @return {void}
         */
        function createPromotion() {
            Promotions.managePromotion()
                .result.then(function () {
                    getPromotions();
                });
        }

        /**
         * Opens edit promotion modal, then merges then updates the local promotion
         * @param  {object} promotion - API promotion item
         * @return {void}
         */
        function editPromotion(promotion) {
            if (promotion.status === 'pending') {
                vm.editedPendingPromotions.push(promotion.id);
            }

            Promotions.managePromotion(promotion.id)
                .result.then(function (updatedPromotion) {

                    // Find and update the promotion on this page
                    angular.forEach(vm.promotions.data, function (promotion) {
                        if (promotion.id === updatedPromotion.id) {
                            if (vm.editedPendingPromotions.indexOf(updatedPromotion.id) !== -1 && updatedPromotion.status === 'approved') {
                                // this promotion has been changed from pending to approved
                                updatedPromotion.recentlyApproved = true;
                            } else {
                                getPromotions();
                            }
                        }
                    });

                }, function (action) {
                    if (action === 'deleted') {
                        getPromotions();
                    }
                });
        }

        // init
        bootstrap();
    }

}(angular));
